/**
 * Batches + AI Optimization controls. When enableBatchesAndAi is off (the default for
 * other drop-config contexts) both render as disabled "Coming soon" rows. Batches and
 * AI Optimization are mutually exclusive; Batches also conflicts with "Use only new domains".
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';

import { AiOptimizationFields } from './AiOptimizationFields';
import { BatchFields } from './BatchFields';
import { ComingSoonRow, OptionRow } from '../OptionRow';
import type { DropOptionValue } from '../DropOptionsSection';

const BATCHES_TIP =
  'Split the send into batches. Turns off AI Optimization; cannot be combined with Use only new domains.';
const AI_OPT_TIP =
  'Keep sending in batches while the chosen metric stays above your minimum, until the cutoff time.';
const BATCHES_LABEL = 'Send in batches';
const AI_OPT_LABEL = 'AI Optimization';

export interface BatchAndAiOptionsProps {
  enableBatchesAndAi: boolean;
  compact: boolean;
  isBusy: boolean;
  allowUnchanged: boolean;
  sendInBatches: boolean | DropOptionValue;
  setSendInBatches: (v: boolean | 'unchanged') => void;
  batchAmount: number | undefined;
  setBatchAmount: (v: number | undefined) => void;
  batchDelay: number | undefined;
  setBatchDelay: (v: number | undefined) => void;
  aiOptimizationEnabled: boolean;
  setAiOptimizationEnabled: (v: boolean) => void;
  aiOptimizationType: 'ROI' | 'CTR' | '';
  setAiOptimizationType: (v: 'ROI' | 'CTR') => void;
  aiOptimizationBatchAmount: number | '';
  setAiOptimizationBatchAmount: (v: number | '') => void;
  aiOptimizationMinimalValuePercent: number | '';
  setAiOptimizationMinimalValuePercent: (v: number | '') => void;
  aiOptimizationSendUntil: string;
  setAiOptimizationSendUntil: (v: string) => void;
  useOnlyNewDomains: boolean | DropOptionValue;
}

export const BatchAndAiOptions: FC<BatchAndAiOptionsProps> = (props) => {
  const { enableBatchesAndAi, compact, isBusy, allowUnchanged } = props;

  if (!enableBatchesAndAi) {
    return (
      <>
        <ComingSoonRow label={BATCHES_LABEL} tooltip={BATCHES_TIP} testId="batches-coming-soon" />
        <ComingSoonRow label={AI_OPT_LABEL} tooltip={AI_OPT_TIP} testId="ai-optimization-coming-soon" />
      </>
    );
  }

  const batchesOn = props.sendInBatches === true;
  const aiOn = props.aiOptimizationEnabled === true;
  const newDomainsOn = props.useOnlyNewDomains === true;

  // Batches and AI Optimization are mutually exclusive — enabling one clears the other so a
  // drop is never saved with both (SMSWiser rejects the combination; the backend also enforces).
  const clearAi = () => {
    props.setAiOptimizationEnabled(false);
    (props.setAiOptimizationType as (v: 'ROI' | 'CTR' | '') => void)('');
    props.setAiOptimizationBatchAmount('');
    props.setAiOptimizationMinimalValuePercent('');
    props.setAiOptimizationSendUntil('');
  };
  const clearBatches = () => {
    props.setSendInBatches(false);
    props.setBatchAmount(undefined);
    props.setBatchDelay(undefined);
  };
  const handleSetBatches = (v: boolean | 'unchanged') => {
    props.setSendInBatches(v);
    if (v === true) clearAi();
  };
  const handleSetAi = (v: boolean) => {
    if (v) clearBatches();
    props.setAiOptimizationEnabled(v);
  };

  return (
    <>
      <OptionRow
        value={props.sendInBatches as DropOptionValue}
        onChange={handleSetBatches}
        label={BATCHES_LABEL}
        tooltip={BATCHES_TIP}
        disabled={isBusy || newDomainsOn}
        allowUnchanged={allowUnchanged}
      />
      {batchesOn && (
        <BatchFields
          batchAmount={props.batchAmount}
          setBatchAmount={props.setBatchAmount}
          batchDelay={props.batchDelay}
          setBatchDelay={props.setBatchDelay}
          isBusy={isBusy}
          compact={compact}
        />
      )}
      <OptionRow
        value={props.aiOptimizationEnabled}
        onChange={(v) => handleSetAi(v === true)}
        label={AI_OPT_LABEL}
        tooltip={AI_OPT_TIP}
        disabled={isBusy}
        // AI is a plain boolean (its setter can't hold 'unchanged'), so it's an on/off toggle
        // even in bulk — "off" already means "leave unchanged" since the bulk payload omits it.
      />
      {aiOn && (
        <AiOptimizationFields
          type={props.aiOptimizationType}
          // The section narrows this setter to ROI|CTR; the Draft Creator's real
          // state setter accepts '' so the user can clear back to "Select…".
          setType={props.setAiOptimizationType as (v: 'ROI' | 'CTR' | '') => void}
          batchAmount={props.aiOptimizationBatchAmount}
          setBatchAmount={props.setAiOptimizationBatchAmount}
          minimalValuePercent={props.aiOptimizationMinimalValuePercent}
          setMinimalValuePercent={props.setAiOptimizationMinimalValuePercent}
          sendUntil={props.aiOptimizationSendUntil}
          setSendUntil={props.setAiOptimizationSendUntil}
          isBusy={isBusy}
          compact={compact}
        />
      )}
    </>
  );
};
